import axios from 'axios';
import router from '@/router';
import { useToast } from '@/composables/useToast';
import { HttpStatus } from '@/constants/http';
import { getAuth0Instance } from '@/plugins/auth0.js';
import { presentAsFormError } from '@/utils/form-errors.js';

const getApiUrl = (path = '') => {
  return `${import.meta.env.VITE_API_ENDPOINT}${path}`;
};

const isCypressTest = () => {
  return import.meta.env.BASE_URL.includes('cypress');
};

const internalApiClient = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

internalApiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const apiClient = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json',
    // Application: process.env.VUE_APP_TOKEN,
  },
});

// Add the Authorization header dynamically before each request
apiClient.interceptors.request.use(async (config) => {
  if (isCypressTest()) return config;

  const auth0 = getAuth0Instance();
  let authToken = null;

  try {
    authToken = await auth0.getAccessTokenSilently();
  } catch (error) {
    console.error('Failed to get access token:', error);
    router.push({ name: 'login' });
  }

  config.headers.authorization = `Bearer ${authToken}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    // For blob responses (file downloads), return the full response to access headers
    if (response.config.responseType === 'blob') {
      return response;
    }

    // extract nested data from response
    // or because search endpoints are not as nested
    // const data = response.data.data || response.data ;
    // return { ...response.data, data };
    return response.data;
  },
  (error) => {
    const { toast } = useToast();
    const status = error.response?.status;
    const errors = error.response?.data?.errors || [];
    // Per-request opt-out: pass `silentStatuses: [404, ...]` or `silentErrorCodes: ['some_code', ...]`
    // in the request config to suppress the generic error toast for expected/anticipated errors.
    const silentStatuses = error.config?.silentStatuses || [];
    const silentErrorCodes = error.config?.silentErrorCodes || [];
    switch (status) {
      case HttpStatus.UNAUTHORIZED:
        router.push({ name: 'login' });
        break;
      case HttpStatus.UNPROCESSABLE_ENTITY:
        toast({
          heading: 'Error saving',
          message: 'Check the form for errors',
          color: 'error',
          icon: 'info-circle',
        });
        break;
      case HttpStatus.FORBIDDEN:
        break;
      default:
        if (silentStatuses.includes(status)) break;
        if (silentErrorCodes.includes(errors[0]?.code)) break;

        // Enables whether to show/hide a toast based on the error code
        if (presentAsFormError(errors)) return Promise.reject(error);

        toast({
          heading: 'Error occurred',
          message: `${errors[0]?.message} ${errors[0]?.meta?.hint || ''}`,
          color: 'error',
          icon: 'info-circle',
        });
    }

    return Promise.reject(error);
  }
);

export { apiClient, internalApiClient, getApiUrl };
